import React, { useEffect, useState } from "react";
import axios from "axios";
import styles from "../styles/AdminPacotes.module.css";
import { Check, X} from "lucide-react";

type Diferencial = {
  id: number;
  tipo: string;
  descricaoCustomizada?: string | null;
  quantidade: number;
};

type PacoteDTO = {
  id?: number;
  titulo: string;
  descricao: string;
  precoBase: number;
  duracaoEmMinutos: number;
  idiomas: string[];
  idadeMinima: number;
  publicoAlvo: string;
  fotoUrls?: string[];
  videoUrls?: string[];
  motoristaNomes?: string[];
  servicoNome?: string;
  diferenciais?: Diferencial[];
  status?: string;
  motivoReprovacao?: string | null;
};

const AdminPacotes = () => {
  const BASE_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const [activeTab, setActiveTab] = useState<"pendentes" | "todos">("pendentes");
  const [pacotesPendentes, setPacotesPendentes] = useState<PacoteDTO[]>([]);
  const [pacotesTodos, setPacotesTodos] = useState<PacoteDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null); // ✅ movido para cá

  const fetchPacotes = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [pendentesRes, todosRes] = await Promise.all([
        axios.get(`${BASE_URL}/admins/pacotes/pendentes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BASE_URL}/admins/pacotes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setPacotesPendentes(pendentesRes.data);
      setPacotesTodos(todosRes.data);
    } catch (err) {
      console.error("Erro ao carregar pacotes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPacotes();
  }, []);

  const atualizarStatus = async (pacoteId: number, status: string, motivo?: string) => {
    try {
      await axios.put(
        `${BASE_URL}/admins/pacotes/${pacoteId}/status`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status, motivo },
        }
      );
      fetchPacotes();
    } catch (err) {
      console.error("Erro ao atualizar status:", err);
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const renderPacotes = (pacotes: PacoteDTO[]) => {
    if (pacotes.length === 0) return <p>Nenhum pacote encontrado.</p>;

    return pacotes.map(p => {
      const isExpanded = expandedId === p.id;

      return (
        <div key={p.id} className={styles.card}>
          {/* Cabeçalho do pacote */}
          <div className={styles.cardHeader} onClick={() => toggleExpand(p.id!)}>
            <h4>{p.titulo}</h4>
            <div className={styles.headerRight}>
              <span
                className={`${styles.statusBadge} ${p.status === "APROVADO"
                  ? styles.aprovado
                  : p.status === "REPROVADO"
                    ? styles.reprovado
                    : styles.pendente
                  }`}
              >
                {p.status ?? "PENDENTE"}
              </span>
              <span className={styles.expandIcon}>{isExpanded ? "▲" : "▼"}</span>
            </div>
          </div>

          {/* Conteúdo expansível */}
          {isExpanded && (
            <div className={styles.cardContent}>
              <p>{p.descricao}</p>
              <p>
                <strong>Preço:</strong> R$ {p.precoBase.toFixed(2)} |{" "}
                <strong>Duração:</strong> {p.duracaoEmMinutos} min |{" "}
                <strong>Idade mínima:</strong> {p.idadeMinima}
              </p>

              {p.servicoNome && (
                <p>
                  <strong>Tipo de serviço:</strong> {p.servicoNome}
                </p>
              )}

              {p.motoristaNomes && p.motoristaNomes.length > 0 && (
                <p>
                  <strong>Motoristas:</strong> {p.motoristaNomes.join(", ")}
                </p>
              )}

              {p.motivoReprovacao && (
                <p>
                  <strong>Motivo reprovação:</strong> {p.motivoReprovacao}
                </p>
              )}

              {p.diferenciais && p.diferenciais.length > 0 && (
                <ul>
                  {p.diferenciais.map(d => (
                    <li key={d.id}>
                      {d.tipo}{" "}
                      {d.descricaoCustomizada ? `(${d.descricaoCustomizada})` : ""} - Qtd:{" "}
                      {d.quantidade}
                    </li>
                  ))}
                </ul>
              )}

              {/* Fotos e vídeos */}
              <div className={styles.mediaContainer}>
                {p.fotoUrls?.map((url, idx) => (
                  <img
                    key={idx}
                    src={`${BASE_URL}${url}`}
                    alt={`Foto ${idx + 1}`}
                    className={styles.foto}
                  />
                ))}
                {p.videoUrls?.map((url, idx) => (
                  <video key={idx} src={`${BASE_URL}${url}`} controls className={styles.video} />
                ))}
              </div>

              {/* Botões de status */}
              <div className={styles.cardButtons}>
                {p.status !== "APROVADO" && (
                  <button
                    className={styles.approve}
                    onClick={() => atualizarStatus(p.id!, "APROVADO")}
                  >
                    <Check size={16} style={{ marginRight: "6px" }} />
                    Aprovar
                  </button>
                )}
                {p.status !== "REPROVADO" && (
                  <button
                    className={styles.reject}
                    onClick={() => {
                      const motivo = prompt("Informe o motivo da reprovação:");
                      if (motivo) atualizarStatus(p.id!, "REPROVADO", motivo);
                    }}
                  >
                    <X size={16} style={{ marginRight: "6px" }} />
                    Reprovar
                  </button>
                )}
                {p.status !== "PENDENTE" && (
                  <button
                    className={styles.pending}
                    onClick={() => atualizarStatus(p.id!, "PENDENTE")}
                  >
                    Voltar para pendente
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div>
      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tabButton} ${activeTab === "pendentes" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("pendentes")}
        >
          Pendentes
        </button>
        <button
          className={`${styles.tabButton} ${activeTab === "todos" ? styles.activeTab : ""}`}
          onClick={() => setActiveTab("todos")}
        >
          Todos
        </button>
      </div>

      {/* Conteúdo da tab */}
      <div className={styles.tabContent}>
        {loading ? (
          <p>Carregando...</p>
        ) : activeTab === "pendentes" ? (
          renderPacotes(pacotesPendentes)
        ) : (
          renderPacotes(pacotesTodos)
        )}
      </div>
    </div>
  );
};

export default AdminPacotes;